import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout/DashboardLayout";
import MatchResultsModal from "../../components/Modal/MatchResultsModal";
import { EMPTY_VALUE, formatDate } from "../../i18n/format";
import { getApiErrorCode, getApiErrorMessage, getApiErrorStatus } from "../../lib/apiError";
import {
  useMatchRequestsCreateCreate,
  useMatchRequestsMyRequestsRetrieve,
  useMatchRequestsRetryCreate,
  useMatchRequestsStartCreate,
  useMatchRequestsTotalMatchResultsRetrieve,
  useMatchRequestsUpdateDraftPartialUpdate,
} from "../../services/match-requests/generated/match-requests/match-requests";
import type {
  MatchRequest,
  MatchRequestCreateRequest,
  RequestTypeEnum,
} from "../../services/match-requests/generated/model";
import { useConversationsCreateCreate } from "../../services/messages/generated/conversations/conversations";
import "./RequestsPage.css";

type TabType = "new" | "my-requests";

const WEIGHT_TOTAL_REQUIRED = 100;

const RequestsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation(["b2b", "common"]);

  const [activeTab, setActiveTab] = useState<TabType>("new");
  const [formData, setFormData] = useState<{
    requestType: RequestTypeEnum;
    requirementTitle: string;
    productServiceName: string;
    hsCode: string;
    quantityVolume: string;
    budgetRange: string;
    timeline: string;
    preferredRegions: string;
    additionalRequirements: string;
  }>({
    requestType: "product",
    requirementTitle: "",
    productServiceName: "",
    hsCode: "",
    quantityVolume: "",
    budgetRange: "",
    timeline: "",
    preferredRegions: "",
    additionalRequirements: "",
  });

  const [criteriaWeights, setCriteriaWeights] = useState({
    industryFit: 25,
    companySize: 15,
    geographicProximity: 20,
    complianceScore: 20,
    productMatch: 10,
    budgetAlignment: 10,
  });

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [readinessBlocked, setReadinessBlocked] = useState(false);
  // WP2.4: 402 = out of credits / plan limit reached — offer the plan page.
  const [needsCredits, setNeedsCredits] = useState(false);

  /**
   * Unified mutation error handler.
   * - WP2.1: 403 with `code=readiness_required` → dedicated readiness banner.
   * - WP2.4: 402 → offer the plan & usage page.
   * Everything else: a generic error from `getApiErrorMessage` with the fallback.
   */
  const showSubmitError = (err: unknown, fallback: string) => {
    if (getApiErrorCode(err) === "readiness_required") {
      setReadinessBlocked(true);
      setNeedsCredits(false);
      setSubmitError(null);
      return;
    }
    setReadinessBlocked(false);
    setNeedsCredits(getApiErrorStatus(err) === 402);
    setSubmitError(getApiErrorMessage(err, fallback));
  };
  const [weightError, setWeightError] = useState<string | null>(null);

  // Fetch match requests using Orval-generated hooks
  const { data: requestsData, isLoading: requestsLoading, error: requestsError } = useMatchRequestsMyRequestsRetrieve();

  // Extract from paginated response
  const requests = (requestsData as any)?.results || [];

  // Mutation hooks
  const createMatchRequestMutation = useMatchRequestsCreateCreate();
  const createConversationMutation = useConversationsCreateCreate();
  const updateDraftMutation = useMatchRequestsUpdateDraftPartialUpdate();
  const startDraftMutation = useMatchRequestsStartCreate();
  const retryMutation = useMatchRequestsRetryCreate();

  // Track which existing draft is being edited; null means a fresh request.
  const [editingDraftId, setEditingDraftId] = useState<number | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleWeightChange = (key: string, value: number) => {
    setCriteriaWeights((prev) => ({ ...prev, [key]: value }));
  };

  const totalWeight = Object.values(criteriaWeights).reduce((sum, weight) => sum + weight, 0);

  const resetForm = () => {
    setFormData({
      requestType: "product",
      requirementTitle: "",
      productServiceName: "",
      hsCode: "",
      quantityVolume: "",
      budgetRange: "",
      timeline: "",
      preferredRegions: "",
      additionalRequirements: "",
    });
    setCriteriaWeights({
      industryFit: 25,
      companySize: 15,
      geographicProximity: 20,
      complianceScore: 20,
      productMatch: 10,
      budgetAlignment: 10,
    });
    setEditingDraftId(null);
  };

  const buildPayload = (isDraft: boolean): MatchRequestCreateRequest => ({
    requestType: formData.requestType,
    requirementTitle: formData.requirementTitle.trim(),
    productServiceName: formData.productServiceName.trim() || null,
    hsCode: formData.hsCode.trim() || null,
    quantityVolume: formData.quantityVolume.trim() || null,
    budgetRange: formData.budgetRange.trim() || null,
    timeline: formData.timeline.trim() || null,
    preferredRegions: formData.preferredRegions.trim() || null,
    additionalRequirements: formData.additionalRequirements.trim() || null,
    industryFit: criteriaWeights.industryFit,
    companySize: criteriaWeights.companySize,
    geographicProximity: criteriaWeights.geographicProximity,
    complianceScore: criteriaWeights.complianceScore,
    productMatch: criteriaWeights.productMatch,
    budgetAlignment: criteriaWeights.budgetAlignment,
    isDraft,
  });

  const validate = (): boolean => {
    setSubmitError(null);
    setReadinessBlocked(false);
    setWeightError(null);
    if (totalWeight !== WEIGHT_TOTAL_REQUIRED) {
      setWeightError(t("requests.errors.weightTotal", { required: WEIGHT_TOTAL_REQUIRED, total: totalWeight }));
      return false;
    }
    if (!formData.requirementTitle.trim()) {
      setSubmitError(t("requests.errors.titleRequired"));
      return false;
    }
    return true;
  };

  const invalidateRequests = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/v1/matchRequests/myRequests/"] });
    queryClient.invalidateQueries({ queryKey: ["/api/v1/matchRequests/totalMatchResults/"] });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    runSave({ asDraft: false });
  };

  // Save flow handles three cases keyed off `editingDraftId` and `asDraft`:
  //   - new draft       (no id, asDraft=true)  → POST create with isDraft=true
  //   - new submission  (no id, asDraft=false) → POST create with isDraft=false
  //   - existing draft  (id set) → PATCH updateDraft, then optionally POST start
  // Keeping it in one function avoids three near-duplicate handlers diverging.
  const runSave = ({ asDraft }: { asDraft: boolean }) => {
    if (!validate()) return;

    const onSubmittedSuccess = () => {
      invalidateRequests();
      setActiveTab("my-requests");
      resetForm();
    };

    if (editingDraftId == null) {
      createMatchRequestMutation.mutate(
        { data: buildPayload(asDraft) },
        {
          onSuccess: onSubmittedSuccess,
          onError: (err) => showSubmitError(err, t("requests.errors.submit")),
        },
      );
      return;
    }

    // Editing an existing draft. Always PATCH the new fields first; if the
    // user clicked "Submit & Run Match Engine", chain a /start/ call so the
    // engine generates results.
    updateDraftMutation.mutate(
      { id: editingDraftId, data: buildPayload(true) },
      {
        onSuccess: () => {
          if (asDraft) {
            onSubmittedSuccess();
            return;
          }
          startDraftMutation.mutate(
            { id: editingDraftId },
            {
              onSuccess: onSubmittedSuccess,
              onError: (err) => showSubmitError(err, t("requests.errors.startDraft")),
            },
          );
        },
        onError: (err) => showSubmitError(err, t("requests.errors.updateDraft")),
      },
    );
  };

  const handleSaveRequest = (asDraft: boolean) => {
    runSave({ asDraft });
  };

  const isMutating =
    createMatchRequestMutation.isPending || updateDraftMutation.isPending || startDraftMutation.isPending;

  const handleEditDraft = (request: MatchRequest) => {
    setEditingDraftId(request.id);
    setFormData({
      requestType: request.requestType === "service" ? "service" : "product",
      requirementTitle: request.requirementTitle ?? "",
      productServiceName: request.productServiceName ?? "",
      hsCode: request.hsCode ?? "",
      quantityVolume: request.quantityVolume ?? "",
      budgetRange: request.budgetRange ?? "",
      timeline: request.timeline ?? "",
      preferredRegions: request.preferredRegions ?? "",
      additionalRequirements: request.additionalRequirements ?? "",
    });
    setCriteriaWeights({
      industryFit: request.industryFit ?? 25,
      companySize: request.companySize ?? 15,
      geographicProximity: request.geographicProximity ?? 20,
      complianceScore: request.complianceScore ?? 20,
      productMatch: request.productMatch ?? 10,
      budgetAlignment: request.budgetAlignment ?? 10,
    });
    setActiveTab("new");
  };

  const handleStartDraft = (id: number) => {
    startDraftMutation.mutate(
      { id },
      {
        onSuccess: () => invalidateRequests(),
        onError: (err) => showSubmitError(err, t("requests.errors.startDraft")),
      },
    );
  };

  const handleRetry = (id: number) => {
    setSubmitError(null);
    retryMutation.mutate(
      { id },
      {
        onSuccess: () => invalidateRequests(),
        onError: (err) => showSubmitError(err, t("requests.errors.retry")),
      },
    );
  };

  const [selectedRequest, setSelectedRequest] = useState<MatchRequest | null>(null);
  const [requestWithResults, setRequestWithResults] = useState<MatchRequest | null>(null);
  const [isMatchResultsModalOpen, setIsMatchResultsModalOpen] = useState(false);

  const formatCreatedDate = (iso: string) => formatDate(iso);

  const handleViewResults = (requestId: number) => {
    const request = requests.find((r: any) => r.id === requestId);
    if (request) {
      setSelectedRequest(request);
      setRequestWithResults(null);
      setIsMatchResultsModalOpen(true);
    }
  };

  // Fetch total match results when modal is open
  const { data: totalMatchResultsData } = useMatchRequestsTotalMatchResultsRetrieve();

  useEffect(() => {
    if (!isMatchResultsModalOpen || !selectedRequest) {
      setRequestWithResults(null);
      return;
    }

    if (totalMatchResultsData?.results) {
      const found = totalMatchResultsData.results.find((d) => d.request?.id === selectedRequest.id);
      if (found?.request?.results != null) {
        setRequestWithResults(found.request);
      } else {
        setRequestWithResults(selectedRequest);
      }
    } else {
      setRequestWithResults(selectedRequest);
    }
  }, [isMatchResultsModalOpen, selectedRequest, totalMatchResultsData]);

  const handleCloseMatchResultsModal = () => {
    setIsMatchResultsModalOpen(false);
    setSelectedRequest(null);
    setRequestWithResults(null);
  };

  return (
    <DashboardLayout>
      <header className="requests__header">
        <div>
          <h1 className="dashboard__title">{t("requests.title")}</h1>
          <p className="dashboard__subtitle">{t("requests.subtitle")}</p>
        </div>
        <div className="requests__tabs">
          <button
            type="button"
            className={`requests__tab ${activeTab === "new" ? "requests__tab--active" : ""}`}
            onClick={() => setActiveTab("new")}
          >
            {t("requests.tabs.new")}
          </button>
          <button
            type="button"
            className={`requests__tab ${activeTab === "my-requests" ? "requests__tab--active" : ""}`}
            onClick={() => setActiveTab("my-requests")}
          >
            {t("requests.tabs.mine", { count: requests.length })}
          </button>
        </div>
      </header>

      {activeTab === "new" && (
        <>
          <div className="requests__content-grid">
            <section className="requests__form-card">
              <div className="requests__card-header">
                <div className="requests__card-icon requests__card-icon--blue">
                  <i className="fa-regular fa-file-lines" />
                </div>
                <h2 className="requests__card-title">{t("requests.form.title")}</h2>
              </div>

              <form id="requests-create-form" className="requests__form" onSubmit={handleSubmit}>
                <div className="requests__form-row">
                  <div className="requests__form-field">
                    <label className="requests__form-label" htmlFor="requirementTitle">
                      {t("requests.form.requirementTitle")} <span className="requests__required">*</span>
                    </label>
                    <input
                      id="requirementTitle"
                      name="requirementTitle"
                      type="text"
                      className="requests__form-input"
                      placeholder={t("requests.form.requirementTitlePlaceholder")}
                      value={formData.requirementTitle}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="requests__form-row">
                  <div className="requests__form-field">
                    <label className="requests__form-label" htmlFor="requestType">
                      {t("requests.form.lookingFor")} <span className="requests__required">*</span>
                    </label>
                    <select
                      id="requestType"
                      name="requestType"
                      className="requests__form-input"
                      value={formData.requestType}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="product">{t("requests.form.aProduct")}</option>
                      <option value="service">{t("requests.form.aService")}</option>
                    </select>
                  </div>
                </div>

                <div className="requests__form-row">
                  <div className="requests__form-field">
                    <label className="requests__form-label" htmlFor="productServiceName">
                      {t("requests.form.productServiceName")} <span className="requests__required">*</span>
                    </label>
                    <input
                      id="productServiceName"
                      name="productServiceName"
                      type="text"
                      className="requests__form-input"
                      placeholder={t("requests.form.productServicePlaceholder")}
                      value={formData.productServiceName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="requests__form-field">
                    <label className="requests__form-label" htmlFor="hsCode">
                      {t("requests.form.hsCode")} <span className="requests__required">*</span>
                    </label>
                    <input
                      id="hsCode"
                      name="hsCode"
                      type="text"
                      className="requests__form-input"
                      placeholder={t("requests.form.hsCodePlaceholder")}
                      value={formData.hsCode}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="requests__form-row">
                  <div className="requests__form-field">
                    <label className="requests__form-label" htmlFor="quantityVolume">
                      {t("requests.form.quantityVolume")}
                    </label>
                    <input
                      id="quantityVolume"
                      name="quantityVolume"
                      type="text"
                      className="requests__form-input"
                      placeholder={t("requests.form.quantityPlaceholder")}
                      value={formData.quantityVolume}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="requests__form-field">
                    <label className="requests__form-label" htmlFor="budgetRange">
                      {t("requests.form.budgetRange")}
                    </label>
                    <input
                      id="budgetRange"
                      name="budgetRange"
                      type="text"
                      className="requests__form-input"
                      placeholder={t("requests.form.budgetPlaceholder")}
                      value={formData.budgetRange}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="requests__form-row">
                  <div className="requests__form-field">
                    <label className="requests__form-label" htmlFor="timeline">
                      {t("requests.form.timeline")}
                    </label>
                    <input
                      id="timeline"
                      name="timeline"
                      type="text"
                      className="requests__form-input"
                      placeholder={t("requests.form.timelinePlaceholder")}
                      value={formData.timeline}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="requests__form-field">
                    <label className="requests__form-label" htmlFor="preferredRegions">
                      {t("requests.form.preferredRegions")}
                    </label>
                    <input
                      id="preferredRegions"
                      name="preferredRegions"
                      type="text"
                      className="requests__form-input"
                      placeholder={t("requests.form.preferredRegionsPlaceholder")}
                      value={formData.preferredRegions}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="requests__form-row">
                  <div className="requests__form-field requests__form-field--full">
                    <label className="requests__form-label" htmlFor="additionalRequirements">
                      {t("requests.form.additionalRequirements")}
                    </label>
                    <textarea
                      id="additionalRequirements"
                      name="additionalRequirements"
                      className="requests__form-textarea"
                      placeholder={t("requests.form.additionalRequirementsPlaceholder")}
                      rows={5}
                      value={formData.additionalRequirements}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </form>
            </section>

            <section className="requests__mcdm-card">
              <div className="requests__card-header">
                <div className="requests__card-icon requests__card-icon--purple">
                  <i className="fa-solid fa-sliders" />
                </div>
                <div>
                  <h2 className="requests__card-title">{t("requests.weighting.title")}</h2>
                  <p className="requests__card-subtitle">{t("requests.weighting.subtitle")}</p>
                </div>
              </div>

              <div className="requests__ahp-section">
                <div className="requests__ahp-badge">
                  <i className="fa-regular fa-circle-info" />
                  <div>
                    <strong>{t("requests.weighting.ahpActive")}</strong>
                    <span>{t("requests.weighting.ahpHint")}</span>
                  </div>
                </div>
              </div>

              <div className="requests__criteria-list">
                {[
                  {
                    key: "industryFit",
                    label: t("requests.criteria.industryFit.label"),
                    description: t("requests.criteria.industryFit.description"),
                    value: criteriaWeights.industryFit,
                  },
                  {
                    key: "companySize",
                    label: t("requests.criteria.companySize.label"),
                    description: t("requests.criteria.companySize.description"),
                    value: criteriaWeights.companySize,
                  },
                  {
                    key: "geographicProximity",
                    label: t("requests.criteria.geographicProximity.label"),
                    description: t("requests.criteria.geographicProximity.description"),
                    value: criteriaWeights.geographicProximity,
                  },
                  {
                    key: "complianceScore",
                    label: t("requests.criteria.complianceScore.label"),
                    description: t("requests.criteria.complianceScore.description"),
                    value: criteriaWeights.complianceScore,
                  },
                  {
                    key: "productMatch",
                    label: t("requests.criteria.productMatch.label"),
                    description: t("requests.criteria.productMatch.description"),
                    value: criteriaWeights.productMatch,
                  },
                  {
                    key: "budgetAlignment",
                    label: t("requests.criteria.budgetAlignment.label"),
                    description: t("requests.criteria.budgetAlignment.description"),
                    value: criteriaWeights.budgetAlignment,
                  },
                ].map((criterion) => (
                  <div key={criterion.key} className="requests__criterion-item">
                    <div className="requests__criterion-info">
                      <h3 className="requests__criterion-label">{criterion.label}</h3>
                      <p className="requests__criterion-description">{criterion.description}</p>
                    </div>
                    <div className="requests__criterion-control">
                      <div className="requests__criterion-slider-wrapper">
                        <div className="requests__criterion-indicator">
                          <div
                            className="requests__criterion-indicator-fill"
                            style={{ width: `${criterion.value}%` }}
                          />
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={criterion.value}
                          onChange={(e) => handleWeightChange(criterion.key, parseInt(e.target.value, 10))}
                          className="requests__criterion-slider"
                        />
                      </div>
                      <span className="requests__criterion-value">{criterion.value}%</span>
                    </div>
                  </div>
                ))}
              </div>

              <div
                className={`requests__total-weight ${totalWeight !== WEIGHT_TOTAL_REQUIRED ? "requests__total-weight--error" : ""}`}
              >
                <div className="requests__total-weight-icon">
                  <i className="fa-regular fa-circle-check" />
                </div>
                <div>
                  <p className="requests__total-weight-label">{t("requests.weighting.totalWeight")}</p>
                  <p className="requests__total-weight-value">{totalWeight}%</p>
                </div>
              </div>
              {weightError && (
                <p className="requests__weight-error" role="alert">
                  {weightError}
                </p>
              )}
            </section>
          </div>

          {readinessBlocked && (
            <div className="requests__submit-error" role="alert">
              <p>{t("requests.list.readinessRequired")}</p>
              <button
                type="button"
                className="requests__footer-button requests__footer-button--secondary"
                onClick={() => navigate("/b2b/readiness")}
              >
                {t("requests.list.readinessRequiredCta")}
              </button>
            </div>
          )}
          {submitError && (
            <div className="requests__submit-error" role="alert">
              {submitError}
              {needsCredits ? (
                <button
                  type="button"
                  className="requests__submit-error-link"
                  onClick={() => navigate("/b2b/plan-usage")}
                >
                  {t("requests.errors.outOfCreditsCta")}
                </button>
              ) : null}
            </div>
          )}
          <div className="requests__footer-actions">
            {editingDraftId != null && (
              <button
                type="button"
                className="requests__footer-button requests__footer-button--secondary"
                onClick={resetForm}
                disabled={isMutating}
              >
                <i className="fa-solid fa-xmark" />
                <span>{t("requests.actions.cancelEdit")}</span>
              </button>
            )}
            <button
              type="button"
              className="requests__footer-button requests__footer-button--secondary"
              onClick={() => handleSaveRequest(true)}
              disabled={isMutating}
            >
              <i className="fa-regular fa-floppy-disk" />
              <span>
                {editingDraftId != null ? t("requests.actions.saveDraft") : t("requests.actions.saveAsDraft")}
              </span>
            </button>
            <button
              type="submit"
              form="requests-create-form"
              className="requests__footer-button requests__footer-button--primary"
              disabled={isMutating}
            >
              {isMutating ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" />
                  <span>{t("requests.actions.submitting")}</span>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-chart-line" />
                  <span>{t("requests.actions.submitAndRun")}</span>
                </>
              )}
            </button>
          </div>
        </>
      )}

      {activeTab === "my-requests" && (
        <div className="requests__my-requests">
          <h2 className="requests__my-requests-title">{t("requests.list.title")}</h2>
          {!!requestsError && (
            <div className="requests__list-error" role="alert">
              {t("requests.errors.load")}
              <button
                type="button"
                className="requests__retry-button"
                onClick={() => {
                  queryClient.invalidateQueries({
                    queryKey: ["/api/v1/matchRequests/myRequests/"],
                  });
                }}
              >
                {t("common:actions.retry")}
              </button>
            </div>
          )}
          {requestsLoading ? (
            <div className="requests__loading">
              <i className="fa-solid fa-spinner fa-spin" aria-hidden />
              <span>{t("requests.list.loading")}</span>
            </div>
          ) : (
            <div className="requests__table-wrapper">
              <table className="requests__table">
                <thead>
                  <tr>
                    <th>{t("requests.list.columns.title")}</th>
                    <th>{t("requests.list.columns.productService")}</th>
                    <th>{t("requests.list.columns.hsCode")}</th>
                    <th>{t("requests.list.columns.dateCreated")}</th>
                    <th>{t("requests.list.columns.status")}</th>
                    <th>{t("requests.list.columns.matches")}</th>
                    <th>{t("requests.list.columns.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="requests__table-empty">
                        {t("requests.list.empty")}
                      </td>
                    </tr>
                  ) : (
                    requests.map((request: any) => (
                      <tr key={request.id}>
                        <td className="requests__table-title">{request.requirementTitle}</td>
                        <td>{request.productServiceName}</td>
                        <td>{request.hsCode}</td>
                        <td>{formatCreatedDate(request.createdAt)}</td>
                        <td>
                          {request.isDraft ? (
                            <span className="requests__status-badge requests__status-badge--draft">
                              {t("requests.list.draftBadge")}
                            </span>
                          ) : (
                            <>
                              <span className={`requests__status-badge requests__status-badge--${request.status}`}>
                                {request.status}
                              </span>
                              {request.status === "failed" && (
                                <p className="requests__failed-message" role="status">
                                  {request.message || t("requests.list.matchingFailed")}
                                </p>
                              )}
                            </>
                          )}
                        </td>
                        <td>{request.results != null ? request.results.length : EMPTY_VALUE}</td>
                        <td>
                          {request.isDraft ? (
                            <div className="requests__row-actions">
                              <button
                                type="button"
                                className="requests__view-results-button"
                                onClick={() => handleEditDraft(request as MatchRequest)}
                              >
                                {t("common:actions.edit")}
                              </button>
                              <button
                                type="button"
                                className="requests__view-results-button requests__view-results-button--primary"
                                onClick={() => handleStartDraft(request.id)}
                                disabled={startDraftMutation.isPending}
                              >
                                {startDraftMutation.isPending
                                  ? t("requests.actions.starting")
                                  : t("requests.actions.startEngine")}
                              </button>
                            </div>
                          ) : request.status === "failed" ? (
                            <button
                              type="button"
                              className="requests__view-results-button requests__view-results-button--primary"
                              onClick={() => handleRetry(request.id)}
                              disabled={retryMutation.isPending}
                            >
                              {retryMutation.isPending && retryMutation.variables?.id === request.id
                                ? t("requests.actions.retrying")
                                : t("common:actions.retry")}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="requests__view-results-button"
                              onClick={() => handleViewResults(request.id)}
                            >
                              {t("requests.actions.viewResults")}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {selectedRequest && (
        <MatchResultsModal
          isOpen={isMatchResultsModalOpen}
          onClose={handleCloseMatchResultsModal}
          request={(requestWithResults ?? selectedRequest) as any}
          dateCreated={formatCreatedDate(selectedRequest.createdAt)}
          onInitiateContact={(matchResultId) => {
            createConversationMutation.mutate(
              { data: { matchResultId } },
              {
                onSuccess: (conv) => {
                  handleCloseMatchResultsModal();
                  navigate("/b2b/messages", {
                    state: { openConversationId: conv.id, isNewConversation: true },
                  });
                },
                onError: () => {
                  // Shown by the global mutation toast (src/lib/queryClient.ts).
                },
              },
            );
          }}
        />
      )}
    </DashboardLayout>
  );
};

export default RequestsPage;
